import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Box, Button, Paper, Alert, Snackbar, Divider, Typography } from '@mui/material';
import { Save, Cancel, DeleteForever as DeleteForeverIcon } from '@mui/icons-material';
import PhotoUpload from '../Common/PhotoUpload';
import PersonalInfoFields from '../PersonalInfoFields';
import { useProfileForm } from '../../hooks/useProfileForm';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useImageUpload } from '../../hooks/useImageUpload';
import AdminApi from 'Service/Admin.api';
import authApi from 'Service/Auth.api';
import { UserInitialStateType } from 'DataTypes/Redux';
import { useSelector } from 'react-redux';
// Removed SkillArr import to use dynamic API data
import { useDispatch } from 'react-redux';
import { setAlertData, startLoading, stopLoading } from '../../../../Redux/Screen/Action';
import { setMyInfo, clearUser } from '../../../../Redux/User/Action';
import { FormateMyInfoData } from 'Pages/Login/Formatter';
import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from 'Utils/Common';
import DeleteProfileModal from 'Components/Dashboard/DeleteProfileModal';
import CommissionModal from './CommissionModal';
import { ReceiptLong, VerifiedUser, PendingActions } from '@mui/icons-material';

const ProfileForm: React.FC = () => {
  const {
    formData,
    errors,
    isDirty,
    isSubmitting,
    updateField,
    setErrors,
    setSubmitting,
    resetForm,
    updateFormData,
    replaceFormData,
  } = useProfileForm();

  const { validateField, validateForm, isFormValid } = useFormValidation();
  const { uploadImage, uploading: imageUploading } = useImageUpload();
  const dispatch = useDispatch<any>();
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const baseUrl = process.env.REACT_APP_BASE_URL || '';

  // Expertise State
  const [availableSkills, setAvailableSkills] = useState<any[]>([]);
  // Document Upload State
  const [selectedDocuments, setSelectedDocuments] = useState<File[]>([]);
  const [documentsUploaded, setDocumentsUploaded] = useState<boolean>(false);

  const [hasProfessional, setHasProfessional] = useState(false);
  const [hasODR, setHasODR] = useState(false);
  const [profAgreementAccepted, setProfAgreementAccepted] = useState(false);
  const [odrAgreementAccepted, setOdrAgreementAccepted] = useState(false);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [additionalExpertise, setAdditionalExpertise] = useState('');
  const [commissionModalOpen, setCommissionModalOpen] = useState(false);

  // States to track initial DB values for the Declaration logic
  const [initialRolesCount, setInitialRolesCount] = useState(0);
  const [initialProfAgreement, setInitialProfAgreement] = useState(false);
  const [initialOdrAgreement, setInitialOdrAgreement] = useState(false);

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const response = await AdminApi.getSkills();
        if (response && response.data) {
          setAvailableSkills(response.data);
        }
      } catch (error) {
        console.error("Error fetching skills for mapping:", error);
      }
    };
    fetchSkills();
    fetchPProfileData();
    updateFormData({
      profilePhoto: myInfo?.profileImageUrl || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myInfo?.id]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleDeleteConfirm = async (payload: any) => {
    try {
      const res: any = await authApi.deleteProfile(payload);
      if (res.success) {
        localStorage.clear();
        dispatch(clearUser() as any);
        window.location.href = "/home.html";
      } else {
        dispatch(
          setAlertData({
            alertMessage: res?.alert?.alertMessage || "Failed to delete profile.",
            openAlert: true,
            alertVariant: "error",
          })
        );
      }
    } catch (error) {
      console.error("Error deleting profile:", error);
      dispatch(
        setAlertData({
          alertMessage: "An unexpected error occurred while deleting the profile.",
          openAlert: true,
          alertVariant: "error",
        })
      );
    } finally {
      setDeleteModalOpen(false);
    }
  };


  const fetchPProfileData = async () => {
    try {
      if (myInfo?.id != null) {
        const response = await AdminApi.getProfile(String(myInfo.id));
        const api = response?.data;

        if (api) {
          const fn = api.firstName || '';
          const ln = api.lastName || '';
          const fullName = [fn, ln].filter(Boolean).join(' ').trim();
          const isoDate = api.dateOfBirth ? new Date(api.dateOfBirth) : null;
          const yyyyMmDd = isoDate ? `${isoDate.getFullYear()}-${String(isoDate.getMonth() + 1).padStart(2, '0')}-${String(isoDate.getDate()).padStart(2, '0')}` : '';

          const organisationName = api?.corporateOrg?.sName || '';

          const skillsArray = Array.isArray(api.professionalSkills)
            ? api.professionalSkills
              .map((s: any) => String(s?.skillName || '').trim())
              .filter(Boolean)
            : [];
          const skillsString = skillsArray.join(', ');

          // Transform API skills to skillEntries format
          const skillEntries = Array.isArray(api.professionalSkills)
            ? api.professionalSkills.map((skill: any, index: number) => ({
              id: `skill_${Date.now()}_${index}`,
              skillName: skill.skillName || '',
              experienceYears: skill.numberOfExperienceYears || 0,
              panIndia: skill.servesPanIndia || false,
              selectedStates: skill.servesStates ? skill.servesStates.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
              description: skill.description || '',
            }))
            : [];

          const uploadedCertificates = Array.isArray(api.professionalCertificates)
            ? api.professionalCertificates.map((c: any) => ({
              id: c.pkProfessionalCertificate,
              file: c.file,
              uploadedAt: c.uploadedAt
            }))
            : [];

          setProfAgreementAccepted(api.isProfessionalAgreementAccepted || false);
          setOdrAgreementAccepted(api.isODRAgreementAccepted || false);

          const profRoles = api.professionalRoles ? api.professionalRoles.split(',').filter(Boolean) : [];
          const hasODRRole = profRoles.some((role: string) => ['mediator', 'arbitrator', 'negotiator'].includes(role));
          const hasProfRole = profRoles.some((role: string) => ['lawyer', 'ca', 'cs', 'other'].includes(role)) || hasODRRole;

          setHasProfessional(hasProfRole);
          setHasODR(hasODRRole);

          setInitialRolesCount(profRoles.length);
          setInitialProfAgreement(api.isProfessionalAgreementAccepted || false);
          setInitialOdrAgreement(api.isODRAgreementAccepted || false);

          replaceFormData({
            firstName: fn,
            lastName: ln,
            name: fullName || '',
            email: api.email || api.userName || '',
            phone: api.phoneNumber || '',
            address: api.address || '',
            city: api.city || '',
            state: api.state || '',
            postalCode: api.postalCode || '',
            dateOfBirth: yyyyMmDd,
            description: api.description || '',
            profilePhoto: api.profilePicturePath
              ? `${baseUrl}${api.profilePicturePath.startsWith('/') ? '' : '/'}${api.profilePicturePath}`
              : undefined,
            highestQualification: api.highestQualification || '',
            skills: skillsString,
            skillsChips: skillsArray,
            skillEntries: skillEntries,
            organisationName,
            uploadedCertificates,
            professionalRoles: profRoles,
            professionalTracks: api.professionalTracks || '',
            entityType: api.corporateOrg?.entityType || '',
            authorisedPerson: api.corporateOrg?.authorisedPerson || '',
            authorisedPersonPhone: api.corporateOrg?.authorisedPersonPhone || '',
            registrationNumber: api.corporateOrg?.registrationNumber || '',
            gstin: api.corporateOrg?.gstin || '',
            pan: api.corporateOrg?.pan || '',
            tan: api.corporateOrg?.tan || ''
          });
        }
      } else {
        throw new Error('User ID is missing');
      }
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
    }
  }

  const handleTrackSelectionChange = useCallback((prof: boolean, odr: boolean, selectedRolesArray: string[]) => {
    setHasProfessional(prof);
    setHasODR(odr);
    updateFormData({
      professionalRoles: selectedRolesArray,
    });
  }, [updateFormData]);

  const handleFieldChange = useCallback((name: string, value: string | any[]) => {
    updateField(name, value);
  }, [updateField]);

  const handleFieldBlur = useCallback((field: string) => {
    const error = validateField(field, formData[field as keyof typeof formData] as string);
    if (error) {
      setErrors({ ...errors, [field]: error });
    }
  }, [validateField, formData, errors, setErrors]);

  const handlePhotoUpload = async (file: File) => {
    try {

      if (!file) {
        console.error('No file selected for upload.');
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        dispatch(setAlertData({
          alertMessage: `The file size exceeds the ${MAX_FILE_SIZE_MB}MB limit.`,
          alertVariant: "error",
          openAlert: true,
        }));
        return;
      }

      const fd = new FormData();
      fd.append("file", file, file.name);
      // Array.from(fd.entries()).forEach(([k, v]) => {
      //   console.log("FormData entry:", k, v);
      // });
      const uploadedUrl = await AdminApi.UploadProfilePicture(file, String(myInfo.id));
      const rawUrl = uploadedUrl?.data?.profilePictureUrl || '';

      // 👇 Attach base URL only if not already a full URL
      const fullUrl = rawUrl.startsWith('http')
        ? rawUrl
        : `${baseUrl}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;

      // updateFormData({ profilePhoto: uploadedUrl.data.profilePictureUrl });
      updateFormData({ profilePhoto: fullUrl });

      dispatch(setMyInfo({
        ...myInfo,
        profileImageUrl: fullUrl,
      }));
      // updateFormData({ profilePhoto: uploadedUrl });
    } catch (error) {
      console.error('Photo upload failed:', error);
      throw error;
    }
  }

  const handleDocumentSubmission = async () => {
    if (selectedDocuments.length === 0) {
      dispatch(setAlertData({
        alertMessage: "Please select at least one file.",
        alertVariant: "error",
        openAlert: true,
      }));
      return;
    }

    const oversizedFiles = selectedDocuments.filter(file => file.size > MAX_FILE_SIZE_BYTES);

    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => f.name).join(", ");
      dispatch(setAlertData({
        alertMessage: `The following files exceed the ${MAX_FILE_SIZE_MB}MB limit: ${fileNames}`,
        alertVariant: "error",
        openAlert: true,
      }));
      return;
    }

    const uploadData = new FormData();
    // Use email and mobile from formData or myInfo
    uploadData.append("emailId", formData.email || "");
    uploadData.append("mobileNumber", formData.phone || myInfo?.phone || "");

    selectedDocuments.forEach((file) => {
      uploadData.append(`files`, file);
    });

    try {
      dispatch(startLoading({ loading: true, loadtext: "Uploading Documents..." }));
      const response = await authApi.uploadProfessionalDocuments(uploadData);
      if (response?.success) {
        dispatch(stopLoading());
        setSelectedDocuments([]);
        setDocumentsUploaded(true);
        dispatch(setAlertData({
          alertMessage: "Documents Uploaded Successfully",
          alertVariant: "success",
          openAlert: true,
        }));
        fetchPProfileData(); // Refresh the document list
      } else {
        dispatch(stopLoading());
        dispatch(setAlertData({
          alertMessage: "Upload failed. Please try again.",
          alertVariant: "error",
          openAlert: true,
        }));
      }
    } catch (error) {
      dispatch(stopLoading());
      dispatch(setAlertData({
        alertMessage: "Upload failed. Please try again.",
        alertVariant: "error",
        openAlert: true,
      }));
      console.error("Error uploading documents:", error);
    }
  };

  const showDeclaration = useMemo(() => {
    if (myInfo?.userType !== 'PROFESSIONAL') return false;
    const isNewProfAgreementNeeded = hasProfessional && !initialProfAgreement;
    const isNewOdrAgreementNeeded = hasODR && !initialOdrAgreement;
    const isNewRoleAdded = (formData.professionalRoles?.length || 0) > initialRolesCount;
    return isNewProfAgreementNeeded || isNewOdrAgreementNeeded || isNewRoleAdded;
  }, [hasProfessional, initialProfAgreement, hasODR, initialOdrAgreement, formData.professionalRoles?.length, initialRolesCount, myInfo?.userType]);

  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();

    // Step 1️⃣: Validate form
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    if (!isFormValid(validationErrors)) {
      dispatch(setAlertData(
        {
          alertMessage: 'Please fix the errors and try again.',
          alertVariant: 'error',
          openAlert: true,
        }
      ));
      return;
    }

    if (myInfo?.userType === 'PROFESSIONAL') {
      if (showDeclaration && !declarationAccepted) {
        dispatch(setAlertData({ alertMessage: 'You must accept the Final Form Declaration.', alertVariant: 'error', openAlert: true }));
        return;
      }
      if (hasProfessional && !profAgreementAccepted) {
        dispatch(setAlertData({ alertMessage: 'You must accept the Professional Empanelment Agreement.', alertVariant: 'error', openAlert: true }));
        return;
      }
      if (hasODR && !odrAgreementAccepted) {
        dispatch(setAlertData({ alertMessage: 'You must accept the ODR Neutral Empanelment Agreement.', alertVariant: 'error', openAlert: true }));
        return;
      }
    }

    setSubmitting(true);

    try {
      // Step 2️⃣: Read firstName and lastName from formData
      let FirstName = (formData.firstName !== undefined ? formData.firstName : "").trim();
      let LastName = (formData.lastName !== undefined ? formData.lastName : "").trim();
      if (!FirstName && formData.name) {
        const nameParts = (formData.name || "").trim().split(" ");
        FirstName = nameParts[0] || "";
        LastName = nameParts.slice(1).join(" ") || "";
      }

      // Step 3️⃣: Conditional payload building based on userType
      let payload;

      if (myInfo?.userType === "PROFESSIONAL") {
        // ✅ PROFESSIONAL payload
        const Skills = (formData.skillEntries || [])
          .filter(skill => skill.skillName)
          .map(skill => {
            const matchedSkill = availableSkills.find(
              s => s.label.toLowerCase() === skill.skillName.toLowerCase()
            );

            return {
              SkillId: matchedSkill ? matchedSkill.id : (matchedSkill ? matchedSkill.PkSkillDetails : null),
              Rating: 0,
              NumberOfExperienceYears: skill.experienceYears || 0,
              Description: skill.description || "",
              ProfessionalFeesPerCallingRs: 0,
              ServesPanIndia: skill.panIndia || false,
              ...(skill.panIndia ? {} : { ServesStates: skill.selectedStates || [] }),
            };
          });

        payload = {
          FirstName,
          LastName,
          Email: formData.email,
          Address: formData.address,
          ContactNumber: formData.phone,
          City: formData.city,
          State: formData.state,
          PostalCode: formData.postalCode,
          DateOfBirth: formData.dateOfBirth,
          ProfilePicturePath: formData.profilePhoto || "",
          HighestQualification: formData.highestQualification || "",
          Description: formData.description || "",
          Skills,
          IsProfessionalAgreementAccepted: profAgreementAccepted,
          IsODRAgreementAccepted: odrAgreementAccepted,
          ProfessionalRoles: formData.professionalRoles || [],
        };
      } else if (myInfo?.userType === "CORPORATEUSER") {
        payload = {
          FirstName,
          LastName,
          Email: formData.email,
          ContactNumber: formData.phone,
          DateOfBirth: formData.dateOfBirth,
          Address: formData.address,
          State: formData.state,
          City: formData.city,
          PostalCode: formData.postalCode,
          EntityType: formData.entityType,
          AuthorisedPerson: formData.authorisedPerson,
          AuthorisedPersonPhone: formData.authorisedPersonPhone,
          RegistrationNumber: formData.registrationNumber,
          GSTIN: formData.gstin,
          PAN: formData.pan,
          TAN: formData.tan,
        };
      }
      else {
        payload = {
          FirstName,
          LastName,
          Email: formData.email,
          ContactNumber: formData.phone,
          DateOfBirth: formData.dateOfBirth,
          Address: formData.address,
          State: formData.state,
          City: formData.city,
          PostalCode: formData.postalCode,
        };
      }


      // Step 5️⃣: API Call
      const response = await AdminApi.ProfileUpdate(
        String(myInfo?.id),
        payload,
        String(myInfo?.userType)
      );

      // setShowSuccessMessage(true);
      if (response?.success) {
        dispatch(setAlertData(
          {
            alertMessage: 'Profile Updated successfully!',
            alertVariant: 'success',
            openAlert: true,
          }
        ));

        // Update Redux State
        dispatch(setMyInfo({
          ...myInfo,
          id: myInfo?.id,
          firstname: FirstName,
          lastname: LastName,
          phone: formData.phone,
        }))
      }
    } catch (error) {
      console.error("❌ Save failed:", error);
      // setShowErrorMessage(true);
      dispatch(setAlertData(
        {
          alertMessage: 'Failed to update profile. Please try again.',
          alertVariant: 'error',
          openAlert: true,
        }
      ));
    } finally {
      setSubmitting(false);
    }
  }, [
    validateForm, formData, setErrors, isFormValid, setSubmitting, myInfo?.id, myInfo?.userType,
    declarationAccepted, hasProfessional, profAgreementAccepted, hasODR, odrAgreementAccepted,
    availableSkills
  ]);


  const handleCancel = useCallback(() => {
    if (isDirty) {
      const confirmCancel = window.confirm('You have unsaved changes. Are you sure you want to cancel?');
      if (confirmCancel) {
        resetForm();
      }
    }
  }, [isDirty, resetForm]);


  return (
    <>
      <Box
        sx={{
          height: ['PROFESSIONAL', 'CORPORATEUSER'].includes(myInfo?.userType)
            ? '75vh'
            : '65vh',
          //         height: myInfo?.userType === 'PROFESSIONAL'
          // ? '75vh'
          // : myInfo?.userType === 'CORPORATEUSER'
          //   ? '75vh'
          //   : '65vh',
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {myInfo?.userType === 'PROFESSIONAL' && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, bgcolor: '#fff', borderBottom: '1px solid #dee2e6' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Professional Empanelment Profile</Typography>
              <Typography variant="body2" color="text.secondary">Complete your details to activate your public profile link.</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

              <Button variant="outlined" color="primary" startIcon={<ReceiptLong />} onClick={() => setCommissionModalOpen(true)}>
                View Commission Schedule
              </Button>
            </Box>
          </Box>
        )}

        {/* Scrollable Form Content */}
        <Paper
          component="form"
          id="profile-form"
          onSubmit={handleSubmit}
          sx={{
            flex: 1,               // take remaining space
            overflowY: "auto",     // scroll inside
            // p: { xs: 2, sm: 3 }
          }}
        >
          <PhotoUpload
            currentPhoto={typeof formData.profilePhoto === 'string' ? formData.profilePhoto : undefined}
            onPhotoUpload={handlePhotoUpload}
            loading={imageUploading}
          />

          <PersonalInfoFields
            formData={formData}
            errors={errors}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            // New Document Upload Props
            onFileChange={setSelectedDocuments}
            onUpload={handleDocumentSubmission}
            files={selectedDocuments}
            isUploaded={documentsUploaded}
            hasProfessional={hasProfessional}
            hasODR={hasODR}
            profAgreementAccepted={profAgreementAccepted}
            odrAgreementAccepted={odrAgreementAccepted}
            declarationAccepted={declarationAccepted}
            onTrackSelectionChange={handleTrackSelectionChange}
            setProfAgreementAccepted={setProfAgreementAccepted}
            setOdrAgreementAccepted={setOdrAgreementAccepted}
            setDeclarationAccepted={setDeclarationAccepted}
            showDeclaration={showDeclaration}
          />

          <Divider sx={{ my: 4, mx: 3 }} />

          <Box sx={{ p: 4, mb: 4, borderRadius: '12px', bgcolor: 'rgba(0, 0, 0, 0.02)', border: '1px dashed #dee2e6' }}>
            <Typography variant="h6" color="error" gutterBottom sx={{ fontWeight: '600', fontSize: '1.1rem' }}>
              Delete Account
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ mb: 3, maxWidth: '600px', lineHeight: 1.6 }}>
              This will permanently remove your profile and all associated data from our system. This action is final and cannot be undone.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteForeverIcon />}
              onClick={() => setDeleteModalOpen(true)}
              sx={{
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                px: 3,
                '&:hover': {
                  bgcolor: 'rgba(211, 47, 47, 0.04)',
                  borderColor: '#d32f2f',
                },
              }}
            >
              Permanently Delete Profile
            </Button>
          </Box>
        </Paper>

        {/* Sticky Footer with Actions */}
        <Box
          sx={{
            borderTop: "1px solid #dee2e6",
            p: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            backgroundColor: "#f8f9fa",
          }}
        >
          {(() => {
            const isSaveDisabled = isSubmitting || !isDirty || (myInfo?.userType === 'PROFESSIONAL' && ((hasProfessional && !profAgreementAccepted) || (hasODR && !odrAgreementAccepted) || (showDeclaration && !declarationAccepted)));
            return (
              <Button
                type="submit"
                variant="contained"
                startIcon={<Save />}
                disabled={isSaveDisabled}
                sx={{
                  backgroundColor: '#007bff',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#0056b3' },
                  '&:disabled': { backgroundColor: '#6c757d' }
                }}
                form="profile-form" // ensures it triggers form submit
              >
                {isSubmitting ? "Saving..." : "Save Profile"}
              </Button>
            );
          })()}

          <Button
            type="button"
            variant="outlined"
            startIcon={<Cancel />}
            onClick={handleCancel}
            disabled={isSubmitting}
            sx={{
              color: '#6c757d',
              borderColor: '#dee2e6',
              '&:hover': {
                backgroundColor: '#f8f9fa',
                borderColor: '#6c757d',
                color: '#495057',
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      <CommissionModal open={commissionModalOpen} onClose={() => setCommissionModalOpen(false)} />
      <DeleteProfileModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );



};

export default ProfileForm;